import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { LayoutGrid, Network, TreePine, GitBranch, UserPlus, Loader2, ImagePlus, RotateCw, Trash2 } from "lucide-react";
import FamilyDiagram from "../components/FamilyDiagram";

const weekday = (d) => { try { return d ? new Date(d).toLocaleDateString(undefined, { weekday: "long" }) : ""; } catch { return ""; } };
const emptySuggestion = { suggester_name: "", first: "", middle: "", last: "", dob: "", dod: "", branch: "", note: "", photo: "", photo_rot: 0, parent_id: "" };

function readAndResize(file, cb) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 420;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      cb(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

export default function PublicTree() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [view, setView] = useState("cards");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [sug, setSug] = useState(emptySuggestion);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let vid = localStorage.getItem("familigi_visitor");
    if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("familigi_visitor", vid); }
    axios.get(`${API}/public/tree/${token}`, { params: { t: Date.now(), v: vid } })
      .then((r) => setState({ loading: false, error: "", data: r.data }))
      .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "This shared tree is not available.", data: null }));
  }, [token]);

  const d = state.data;
  const people = d?.people || [];
  const filtered = useMemo(() => {
    const toks = q.toLowerCase().trim().split(/\s+/).filter(Boolean);
    if (!toks.length) return people;
    return people.filter((p) => {
      const hay = [p.first, p.middle, p.last, p.branch, p.dob, p.dod].filter(Boolean).join(" ").toLowerCase();
      return toks.every((t) => hay.includes(t));
    });
  }, [people, q]);

  const submitSuggestion = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/tree/${token}/suggest`, sug);
      setSent(true); setSug(emptySuggestion);
      setTimeout(() => { setOpen(false); setSent(false); }, 1800);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not send your suggestion.");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return <div className="grid min-h-screen place-items-center text-[#161616] dark:text-white"><Loader2 className="animate-spin opacity-60" /></div>;
  }
  if (state.error) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#f4f4f4] p-6 text-center dark:bg-[#0b0b0f] dark:text-white" data-testid="public-tree-error">
        <div>
          <TreePine className="mx-auto mb-3 opacity-40" size={40} />
          <h1 className="font-head text-2xl">Link unavailable</h1>
          <p className="mt-2 opacity-70">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f4f4] text-[#161616] dark:bg-[#0b0b0f] dark:text-white" data-testid="public-tree">
      <header className="border-b border-black/10 bg-white/70 backdrop-blur dark:border-white/10 dark:bg-black/30">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-5 py-4">
          <span className="font-head text-lg font-bold text-[var(--primary)]">Familigi™</span>
          <span className="hidden text-xs opacity-50 sm:inline">Full Option Family Office</span>
          <div className="ms-auto flex items-center gap-3">
            <Button onClick={() => setOpen(true)} data-testid="public-suggest-open"><UserPlus size={16} /> Suggest an addition</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-5 py-8">
        <div className="flex flex-wrap items-center gap-3">
          <TreePine className="text-[var(--primary)]" size={22} />
          <h1 className="font-head text-2xl sm:text-3xl" data-testid="public-tree-name">{d.tree.name}</h1>
          <Badge color={d.tree.type === "generic" ? "info" : "success"}>{d.tree.type === "generic" ? "GENERIC" : "FAMILY"}</Badge>
          <span className="text-xs opacity-60">{d.count} record(s) · read-only</span>
          <div className="ms-auto flex rounded-md border border-black/15 dark:border-white/15 p-0.5">
            <button onClick={() => setView("cards")} data-testid="public-view-cards" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${view === "cards" ? "bg-[var(--primary)] text-white" : ""}`}><LayoutGrid size={15} /> Cards</button>
            <button onClick={() => setView("diagram")} data-testid="public-view-diagram" className={`flex items-center gap-1 rounded px-2.5 py-1 text-sm ${view === "diagram" ? "bg-[var(--primary)] text-white" : ""}`}><Network size={15} /> Diagram</button>
          </div>
        </div>

        <div className="relative max-w-md">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="public-search"
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
        </div>

        {view === "diagram" ? (
          <FamilyDiagram people={filtered} descendants={new Set()} focus={null} showMaiden={false}
            onConnectParent={() => {}} onMove={() => {}} orientation="portrait" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.length === 0 && <Card className="opacity-60">No records to show.</Card>}
            {filtered.map((p) => (
              <Card key={p.id} className="space-y-2" style={{ borderInlineStart: `4px solid ${p.color || "#0F62FE"}` }} data-testid={`public-person-${p.id}`}>
                <div className="flex items-center gap-2">
                  {p.photo && (
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-black/10 dark:border-white/15">
                      <img src={p.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${p.photo_rot || 0}deg)` }} />
                    </div>
                  )}
                  <div className="font-head text-lg truncate">{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {d.tree.type === "family" && <Badge color="info"><GitBranch size={11} className="inline" /> {p.branch || "Main"}</Badge>}
                  {p.dob && <Badge>b. {weekday(p.dob)} {p.dob}</Badge>}
                  {p.dod && <Badge color="danger">d. {weekday(p.dod)} {p.dod}</Badge>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Modal open={open} onClose={() => setOpen(false)} title="Suggest an addition" testid="public-suggest-modal">
        {sent ? (
          <div className="py-6 text-center" data-testid="public-suggest-sent">
            <div className="mb-2 text-3xl">🌿</div>
            <p className="font-head text-lg">Thank you!</p>
            <p className="opacity-70">Your suggestion was sent to the tree owner for review.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm opacity-70">Suggest someone to add to <b>{d.tree.name}</b>. The owner reviews every suggestion before it appears.</p>
            <Input label="Your name" maxLength={60} value={sug.suggester_name} onChange={(e) => setSug({ ...sug, suggester_name: e.target.value })} data-testid="sug-name" />
            <div className="grid grid-cols-3 gap-2">
              <Input label="First (15)" maxLength={15} value={sug.first} onChange={(e) => setSug({ ...sug, first: e.target.value })} data-testid="sug-first" />
              <Input label="Middle (15)" maxLength={15} value={sug.middle} onChange={(e) => setSug({ ...sug, middle: e.target.value })} data-testid="sug-middle" />
              <Input label="Last (15)" maxLength={15} value={sug.last} onChange={(e) => setSug({ ...sug, last: e.target.value })} data-testid="sug-last" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Date of birth" type="date" value={sug.dob} onChange={(e) => setSug({ ...sug, dob: e.target.value })} data-testid="sug-dob" />
              <Input label="Date of death" type="date" value={sug.dod} onChange={(e) => setSug({ ...sug, dod: e.target.value })} data-testid="sug-dod" />
            </div>
            <Input label="Branch / relationship (optional)" maxLength={40} value={sug.branch} onChange={(e) => setSug({ ...sug, branch: e.target.value })} data-testid="sug-branch" />
            <Select label="Child of (optional)" value={sug.parent_id} onChange={(e) => setSug({ ...sug, parent_id: e.target.value })} data-testid="sug-parent">
              <option value="">— not sure / none —</option>
              {people.map((p) => <option key={p.id} value={p.id}>{[p.first, p.middle, p.last].filter(Boolean).join(" ") || "Unnamed"}</option>)}
            </Select>
            <div className="flex items-center gap-3 rounded-md border border-black/10 dark:border-white/10 p-2">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-black/15 dark:border-white/15 bg-black/5 dark:bg-white/5 flex items-center justify-center">
                {sug.photo
                  ? <img src={sug.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: `rotate(${sug.photo_rot || 0}deg) scale(1.42)`, transformOrigin: "center center" }} data-testid="sug-photo-preview" />
                  : <ImagePlus size={20} className="opacity-40" />}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 dark:border-white/15 px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5">
                  <ImagePlus size={15} /> {sug.photo ? "Change photo" : "Add a photo (optional)"}
                  <input type="file" accept="image/*" className="hidden" data-testid="sug-photo"
                    onChange={(e) => readAndResize(e.target.files?.[0], (url) => setSug((s) => ({ ...s, photo: url, photo_rot: 0 })))} />
                </label>
                {sug.photo && (
                  <div className="flex items-center gap-2">
                    <RotateCw size={14} className="opacity-60" />
                    <input type="range" min={0} max={360} value={sug.photo_rot || 0} data-testid="sug-photo-rot"
                      onChange={(e) => setSug({ ...sug, photo_rot: Number(e.target.value) })} className="flex-1 accent-[var(--primary)]" />
                    <span className="w-10 text-end text-xs tabular-nums opacity-70">{sug.photo_rot || 0}°</span>
                    <button type="button" onClick={() => setSug({ ...sug, photo: "", photo_rot: 0 })} data-testid="sug-photo-remove" className="text-xs opacity-60 hover:text-[#DA1E28]"><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
            </div>
            <label className="block w-full">
              <span className="mb-1 block text-xs font-medium opacity-70">Note to the owner (optional)</span>
              <textarea maxLength={500} rows={3} value={sug.note} onChange={(e) => setSug({ ...sug, note: e.target.value })} data-testid="sug-note"
                className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-3 py-2 text-sm" />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submitSuggestion} disabled={busy || !sug.suggester_name.trim()} data-testid="sug-submit">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Send suggestion
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
